import { useCallback, useEffect, useRef, useState } from "react";
import {
  SessionManager,
  type ConnectionState,
  type SessionManagerCallbacks,
} from "../SessionManager";
import type { WsMessage } from "../types";

export interface UseSessionManagerOptions {
  /** WebSocket URL (API Gateway endpoint). */
  url: string;
  /** Called for every inbound WebSocket message. */
  onMessage: (msg: WsMessage) => void;
}

export interface UseSessionManagerResult {
  /** Current connection state. */
  connectionState: ConnectionState;
  /** Send a JSON payload (e.g. design description). */
  send: (data: Record<string, unknown>) => void;
  /** Manually trigger a connect (called automatically on mount). */
  connect: () => void;
  /** Cleanly disconnect. */
  disconnect: () => void;
}

/**
 * React hook that owns a `SessionManager` instance.
 *
 * - Creates the manager once and persists it across renders via a ref.
 * - Exposes reactive `connectionState`.
 * - Cleans up on unmount.
 */
export function useSessionManager({
  url,
  onMessage,
}: UseSessionManagerOptions): UseSessionManagerResult {
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("disconnected");

  // Keep latest onMessage in a ref so the SessionManager always calls
  // the current version without needing to recreate the socket.
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const managerRef = useRef<SessionManager | null>(null);

  // Build the manager lazily (once).
  const getManager = useCallback((): SessionManager => {
    if (!managerRef.current) {
      const callbacks: SessionManagerCallbacks = {
        onMessage: (msg) => onMessageRef.current(msg),
        onConnect: () => setConnectionState("connected"),
        onDisconnect: () => setConnectionState("reconnecting"),
        onStateChange: (s) => setConnectionState(s),
      };
      managerRef.current = new SessionManager(url, callbacks);
    }
    return managerRef.current;
  }, [url]);

  // Connect on mount, disconnect on unmount.
  useEffect(() => {
    const mgr = getManager();
    mgr.connect();
    return () => {
      mgr.disconnect();
      managerRef.current = null;
    };
  }, [getManager]);

  const send = useCallback(
    (data: Record<string, unknown>) => getManager().send(data),
    [getManager]
  );

  const connect = useCallback(() => getManager().connect(), [getManager]);
  const disconnect = useCallback(() => getManager().disconnect(), [getManager]);

  return { connectionState, send, connect, disconnect };
}
